import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from './utils';

export const DatabaseTab = ({ visible, authHeaders }) => {
  const [dbTables, setDbTables] = useState([]);
  const [dbSelectedTable, setDbSelectedTable] = useState('');
  const [dbData, setDbData] = useState(null);
  const [dbLoading, setDbLoading] = useState(false);
  const [dbError, setDbError] = useState(null);
  const [dbPage, setDbPage] = useState(1);
  const [dbSearch, setDbSearch] = useState('');
  const [dbSearchInput, setDbSearchInput] = useState('');
  const [dbEditingRow, setDbEditingRow] = useState(null);
  const [dbEditValues, setDbEditValues] = useState({});
  const [dbActionLoading, setDbActionLoading] = useState(false);
  const [dbSuccess, setDbSuccess] = useState(null);
  const [dbEditModalOpen, setDbEditModalOpen] = useState(false);
  const [dbFilterColumn, setDbFilterColumn] = useState('');
  const [dbFilterValue, setDbFilterValue] = useState('');
  const [dbSortColumn, setDbSortColumn] = useState('');
  const [dbSortDir, setDbSortDir] = useState('asc');

  const loadDbTables = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/database/tables`, {
        headers: authHeaders(),
      });
      setDbTables(res.data.tables || []);
    } catch (err) {
      setDbError(err.response?.data?.error || err.message || 'Failed to load tables');
    }
  }, [authHeaders]);

  const loadDbData = useCallback(async (table, page = 1, search = '', filterColumn = '', filterValue = '', sortColumn = '', sortDir = 'asc') => {
    if (!table) return;
    setDbLoading(true);
    setDbError(null);
    setDbEditingRow(null);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: '50' });
      if (search.trim()) params.set('search', search.trim());
      if (filterColumn && filterValue) {
        params.set('filterColumn', filterColumn);
        params.set('filterValue', filterValue);
      }
      if (sortColumn) {
        params.set('sortColumn', sortColumn);
        params.set('sortDir', sortDir);
      }
      const res = await axios.get(
        `${API_URL}/api/admin/database/tables/${encodeURIComponent(table)}?${params}`,
        { headers: authHeaders() },
      );
      setDbData(res.data);
    } catch (err) {
      setDbError(err.response?.data?.error || err.message || 'Failed to load data');
      setDbData(null);
    } finally {
      setDbLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (visible) loadDbTables();
  }, [visible, loadDbTables]);

  useEffect(() => {
    if (visible && dbSelectedTable) {
      loadDbData(dbSelectedTable, dbPage, dbSearch, dbFilterColumn, dbFilterValue, dbSortColumn, dbSortDir);
    }
  }, [visible, dbSelectedTable, dbPage, dbSearch, dbFilterColumn, dbFilterValue, dbSortColumn, dbSortDir, loadDbData]);

  const handleDbTableChange = (table) => {
    setDbSelectedTable(table);
    setDbPage(1);
    setDbSearch('');
    setDbSearchInput('');
    setDbFilterColumn('');
    setDbFilterValue('');
    setDbSortColumn('');
    setDbSortDir('asc');
    setDbData(null);
    setDbEditingRow(null);
    setDbSuccess(null);
    setDbError(null);
  };

  const handleDbSort = (columnName) => {
    if (dbSortColumn === columnName) {
      setDbSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setDbSortColumn(columnName);
      setDbSortDir('asc');
    }
    setDbPage(1);
  };

  const handleDbSearch = () => {
    setDbSearch(dbSearchInput);
    setDbFilterColumn('');
    setDbFilterValue('');
    setDbPage(1);
  };

  const handleDbEdit = (rowIndex) => {
    setDbEditingRow(rowIndex);
    const row = dbData.rows[rowIndex];
    const editVals = {};
    for (const col of dbData.columns) {
      if (!(dbData.hiddenColumns || []).includes(col.column_name)) {
        const raw = row[col.column_name];
        editVals[col.column_name] = raw === null || raw === undefined
          ? ''
          : typeof raw === 'object'
            ? JSON.stringify(raw)
            : String(raw);
      }
    }
    setDbEditValues(editVals);
    setDbEditModalOpen(true);
    setDbSuccess(null);
  };

  const handleDbCancelEdit = () => {
    setDbEditingRow(null);
    setDbEditValues({});
    setDbEditModalOpen(false);
  };

  const handleDbSave = async () => {
    if (dbEditingRow === null || !dbData) return;
    setDbActionLoading(true);
    setDbError(null);
    try {
      const originalRow = dbData.rows[dbEditingRow];
      const pkValues = {};
      for (const pk of dbData.primaryKey) {
        pkValues[pk] = originalRow[pk];
      }
      const updates = {};
      for (const [key, val] of Object.entries(dbEditValues)) {
        if (!dbData.primaryKey.includes(key) && val !== originalRow[key]) {
          updates[key] = val === '' ? null : val;
        }
      }
      if (Object.keys(updates).length === 0) {
        setDbEditingRow(null);
        setDbEditModalOpen(false);
        return;
      }
      const { data } = await axios.put(
        `${API_URL}/api/admin/database/tables/${encodeURIComponent(dbSelectedTable)}`,
        { pkValues, updates },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
      );
      const embedMsg = data.reembedded === 'pending' ? ' Re-embedding in background.' : '';
      setDbEditingRow(null);
      setDbEditModalOpen(false);
      await loadDbData(dbSelectedTable, dbPage, dbSearch, dbFilterColumn, dbFilterValue, dbSortColumn, dbSortDir);
      setDbSuccess(`Row updated successfully.${embedMsg}`);
    } catch (err) {
      setDbError(err.response?.data?.error || err.message || 'Failed to update row');
    } finally {
      setDbActionLoading(false);
    }
  };

  const handleDbDelete = async (rowIndex) => {
    if (!dbData) return;
    const row = dbData.rows[rowIndex];
    const pkDesc = dbData.primaryKey.map((pk) => `${pk}=${row[pk]}`).join(', ');
    if (!window.confirm(`Delete row (${pkDesc}) from "${dbSelectedTable}"? This cannot be undone.`)) return;
    setDbActionLoading(true);
    setDbError(null);
    setDbSuccess(null);
    try {
      const pkValues = {};
      for (const pk of dbData.primaryKey) {
        pkValues[pk] = row[pk];
      }
      await axios.delete(
        `${API_URL}/api/admin/database/tables/${encodeURIComponent(dbSelectedTable)}`,
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' }, data: { pkValues } },
      );
      await loadDbData(dbSelectedTable, dbPage, dbSearch, dbFilterColumn, dbFilterValue, dbSortColumn, dbSortDir);
      setDbSuccess('Row deleted successfully.');
    } catch (err) {
      setDbError(
        err.response?.data?.error
        || err.response?.data?.details
        || err.message
        || 'Failed to delete row',
      );
    } finally {
      setDbActionLoading(false);
    }
  };

  /** Navigate to a FK reference: switch table and filter by exact ID */
  const handleDbFkClick = (refTable, refColumn, refValue) => {
    setDbSelectedTable(refTable);
    setDbPage(1);
    setDbSearch('');
    setDbSearchInput('');
    setDbFilterColumn(refColumn);
    setDbFilterValue(String(refValue));
    setDbData(null);
    setDbEditingRow(null);
    setDbEditModalOpen(false);
    setDbSuccess(null);
    setDbError(null);
  };

  const getVisibleColumns = () => {
    if (!dbData) return [];
    const hidden = dbData.hiddenColumns || [];
    return dbData.columns.filter((c) => !hidden.includes(c.column_name));
  };

  const getFkInfo = (colName) => {
    if (!dbData?.foreignKeys) return null;
    return dbData.foreignKeys.find((fk) => fk.column_name === colName) || null;
  };

  const getFkLabel = (colName, value) => {
    if (value == null || !dbData?.fkLabels?.[colName]) return null;
    return dbData.fkLabels[colName][String(value)] || null;
  };

  const isLongTextColumn = (colName, dataType) => {
    const longTypes = ['text'];
    if (longTypes.includes(dataType)) return true;
    const longNames = ['bio', 'description', 'content', 'body', 'notes', 'metadata'];
    return longNames.some((n) => colName.toLowerCase().includes(n));
  };

  return (
    <>
      {dbError && (
        <div className="AdminPortal-banner AdminPortal-banner--error" role="alert">
          {dbError}
        </div>
      )}
      {dbSuccess && (
        <div className="AdminPortal-banner AdminPortal-banner--success" role="status">
          {dbSuccess}
        </div>
      )}

      <div className="AdminPortal-dbToolbar">
        <label className="AdminPortal-field AdminPortal-dbTableField">
          <span className="AdminPortal-fieldLabel">Table</span>
          <select
            className="AdminPortal-select AdminPortal-dbTableSelect"
            value={dbSelectedTable}
            onChange={(e) => handleDbTableChange(e.target.value)}
          >
            <option value="">-- Select a table --</option>
            {dbTables.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </label>

        {dbSelectedTable && (
          <div className="AdminPortal-dbSearchRow">
            <input
              type="text"
              className="AdminPortal-input AdminPortal-dbSearchInput"
              placeholder={`Search ${dbSelectedTable}...`}
              value={dbSearchInput}
              onChange={(e) => setDbSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleDbSearch()}
            />
            <button type="button" className="ChatButton" onClick={handleDbSearch}>
              Search
            </button>
            {(dbSearch || dbFilterColumn) && (
              <button
                type="button"
                className="ChatButton"
                onClick={() => { setDbSearchInput(''); setDbSearch(''); setDbFilterColumn(''); setDbFilterValue(''); setDbPage(1); }}
              >
                Clear
              </button>
            )}
            <button
              type="button"
              className="ChatButton"
              onClick={() => loadDbData(dbSelectedTable, dbPage, dbSearch)}
              disabled={dbLoading}
            >
              Refresh
            </button>
          </div>
        )}
      </div>

      {!dbSelectedTable && (
        <p className="AdminPortal-intro">
          Select a table above to browse and manage data in the archive database.
          Tables are discovered automatically -- any new table added to the database will appear here.
        </p>
      )}

      {dbLoading && !dbData && <p className="AdminPortal-intro">Loading data...</p>}

      {dbData && (() => {
        const visibleCols = getVisibleColumns();
        return (
          <div className={dbLoading ? 'AdminPortal-dbReloading' : ''}>
            <div className="AdminPortal-dbMeta">
              <span>{dbData.total} row{dbData.total !== 1 ? 's' : ''} total</span>
              {dbLoading && <span className="AdminPortal-dbReloadingBadge"> · refreshing…</span>}
              {dbSearch && <span> matching &quot;{dbSearch}&quot;</span>}
              {dbFilterColumn && (
                <span className="AdminPortal-dbFilterBadge">
                  {' '}filtered by {dbFilterColumn} = {dbFilterValue}
                  <button
                    type="button"
                    className="AdminPortal-dbFilterClear"
                    onClick={() => { setDbFilterColumn(''); setDbFilterValue(''); setDbPage(1); }}
                    title="Clear filter"
                  >
                    x
                  </button>
                </span>
              )}
              <span> &middot; Page {dbData.page} of {Math.max(1, Math.ceil(dbData.total / dbData.pageSize))}</span>
              {(dbData.hiddenColumns || []).length > 0 && (
                <span className="AdminPortal-dbHiddenNote">
                  {' '}&middot; {dbData.hiddenColumns.length} column{dbData.hiddenColumns.length !== 1 ? 's' : ''} hidden (embeddings)
                </span>
              )}
            </div>

            <div className="AdminPortal-tableWrap AdminPortal-dbTableWrap">
              <table className="AdminPortal-table AdminPortal-dbTable">
                <thead>
                  <tr>
                    {visibleCols.map((col) => {
                      const fk = getFkInfo(col.column_name);
                      const isSorted = dbSortColumn === col.column_name;
                      const sortIndicator = isSorted ? (dbSortDir === 'asc' ? ' ▲' : ' ▼') : '';
                      return (
                        <th
                          key={col.column_name}
                          scope="col"
                          className="AdminPortal-dbSortableHeader"
                          onClick={() => handleDbSort(col.column_name)}
                          title={`Sort by ${col.column_name}`}
                        >
                          <span className="AdminPortal-dbColName">
                            {col.column_name}
                            {fk && (
                              <span className="AdminPortal-dbFkBadge" title={`FK -> ${fk.referenced_table}.${fk.referenced_column}`}>
                                FK
                              </span>
                            )}
                            {sortIndicator && (
                              <span className="AdminPortal-dbSortIndicator">{sortIndicator}</span>
                            )}
                          </span>
                          <span className="AdminPortal-dbColType">{col.data_type}</span>
                        </th>
                      );
                    })}
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {dbData.rows.length === 0 ? (
                    <tr>
                      <td colSpan={visibleCols.length + 1} className="AdminPortal-tableEmpty">
                        No rows found.
                      </td>
                    </tr>
                  ) : (
                    dbData.rows.map((row, rowIdx) => (
                      <tr key={rowIdx}>
                        {visibleCols.map((col) => {
                          const isPk = dbData.primaryKey.includes(col.column_name);
                          const rawVal = row[col.column_name];
                          const displayVal = rawVal === null || rawVal === undefined
                            ? ''
                            : typeof rawVal === 'object'
                              ? JSON.stringify(rawVal)
                              : String(rawVal);
                          const fk = getFkInfo(col.column_name);
                          const fkLabel = fk ? getFkLabel(col.column_name, rawVal) : null;

                          return (
                            <td key={col.column_name} className={isPk ? 'AdminPortal-mono' : ''}>
                              {fk && rawVal != null ? (
                                <span className="AdminPortal-dbFkCell">
                                  <button
                                    type="button"
                                    className="AdminPortal-dbFkLink"
                                    onClick={() => handleDbFkClick(fk.referenced_table, fk.referenced_column, rawVal)}
                                    title={`View in ${fk.referenced_table}`}
                                  >
                                    {fkLabel || displayVal}
                                  </button>
                                  {fkLabel && (
                                    <span className="AdminPortal-dbFkId">id: {displayVal}</span>
                                  )}
                                </span>
                              ) : (
                                <span className="AdminPortal-dbCellValue" title={displayVal}>
                                  {displayVal || <span className="AdminPortal-dbNull">NULL</span>}
                                </span>
                              )}
                            </td>
                          );
                        })}
                        <td className="AdminPortal-dbActions">
                          <button
                            type="button"
                            className="ChatButton"
                            onClick={() => handleDbEdit(rowIdx)}
                            disabled={dbActionLoading}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="ChatButton AdminPortal-delete"
                            onClick={() => handleDbDelete(rowIdx)}
                            disabled={dbActionLoading}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {dbData.total > dbData.pageSize && (
              <div className="AdminPortal-dbPagination">
                <button
                  type="button"
                  className="ChatButton"
                  disabled={dbPage <= 1}
                  onClick={() => setDbPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span className="AdminPortal-dbPageInfo">
                  Page {dbData.page} of {Math.ceil(dbData.total / dbData.pageSize)}
                </span>
                <button
                  type="button"
                  className="ChatButton"
                  disabled={dbPage >= Math.ceil(dbData.total / dbData.pageSize)}
                  onClick={() => setDbPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        );
      })()}

      {dbEditModalOpen && dbEditingRow !== null && dbData && (() => {
        const row = dbData.rows[dbEditingRow];
        const visibleCols = getVisibleColumns();
        const pkDesc = dbData.primaryKey.map((pk) => `${pk}: ${row[pk]}`).join(', ');
        return (
          <div className="AdminPortal-dbModalOverlay" onClick={handleDbCancelEdit}>
            <div className="AdminPortal-dbModal" onClick={(e) => e.stopPropagation()}>
              <div className="AdminPortal-dbModalHeader">
                <h3 className="AdminPortal-dbModalTitle">
                  Edit row in <strong>{dbSelectedTable}</strong>
                </h3>
                <span className="AdminPortal-dbModalPk">{pkDesc}</span>
                <button
                  type="button"
                  className="AdminPortal-dbModalClose"
                  onClick={handleDbCancelEdit}
                  aria-label="Close"
                >
                  X
                </button>
              </div>
              <div className="AdminPortal-dbModalBody">
                {visibleCols.map((col) => {
                  const isPk = dbData.primaryKey.includes(col.column_name);
                  const val = dbEditValues[col.column_name] ?? '';
                  const useLargeInput = isLongTextColumn(col.column_name, col.data_type);
                  const fk = getFkInfo(col.column_name);

                  return (
                    <label key={col.column_name} className="AdminPortal-field AdminPortal-dbModalField">
                      <span className="AdminPortal-fieldLabel">
                        {col.column_name}
                        {isPk && <span className="AdminPortal-dbModalPkTag">PK</span>}
                        {fk && (
                          <span className="AdminPortal-dbFkBadge" title={`FK -> ${fk.referenced_table}.${fk.referenced_column}`}>
                            FK &rarr; {fk.referenced_table}
                          </span>
                        )}
                        <span className="AdminPortal-dbColType" style={{ marginLeft: 6, display: 'inline' }}>
                          {col.data_type}
                        </span>
                      </span>
                      {isPk ? (
                        <input
                          type="text"
                          className="AdminPortal-input"
                          value={val}
                          disabled
                        />
                      ) : useLargeInput ? (
                        <textarea
                          className="AdminPortal-textarea AdminPortal-dbModalTextarea"
                          value={val}
                          rows={5}
                          onChange={(e) =>
                            setDbEditValues((prev) => ({
                              ...prev,
                              [col.column_name]: e.target.value,
                            }))
                          }
                        />
                      ) : (
                        <input
                          type="text"
                          className="AdminPortal-input"
                          value={val}
                          onChange={(e) =>
                            setDbEditValues((prev) => ({
                              ...prev,
                              [col.column_name]: e.target.value,
                            }))
                          }
                        />
                      )}
                    </label>
                  );
                })}
              </div>
              <div className="AdminPortal-dbModalFooter">
                <button
                  type="button"
                  className="ChatButton"
                  onClick={handleDbCancelEdit}
                  disabled={dbActionLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="ChatButton AdminPortal-dbSaveBtn"
                  onClick={handleDbSave}
                  disabled={dbActionLoading}
                >
                  {dbActionLoading ? 'Saving...' : 'Save changes'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
};
